import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
} from '@mui/material';
import Loader from '../../common/Loader';
import { apiConstants, post, get } from '../../common/constants';
import { getMerchant } from '../../helpers/ReduxHelpers';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { manageStatusNotifications } from '../../helpers/UtilityHelper';
import { primaryBtnSx } from '../../styles/patterns';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE, BORDER } from '../../styles/foundation/colors';

const headerCellSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.bold,
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: SUBTLE,
  borderBottom: `2px solid ${BORDER}`,
  py: 1.5,
  px: 2,
};

const eventCellSx = {
  py: 1.5,
  px: 2,
  borderBottom: `1px solid ${BORDER}`,
  width: '15%',
};

const msgCellSx = {
  py: 1.5,
  px: 2,
  borderBottom: `1px solid ${BORDER}`,
  width: '45%',
};

const checkCellSx = {
  py: 1,
  px: 1,
  borderBottom: `1px solid ${BORDER}`,
  width: '13%',
};

// Renders the event × channel subscription matrix. Defaults are the forward B2C subscription;
// RvpNotification reuses this component with the RVP endpoints and event list instead of cloning it.
const B2CNotification = ({
  getEndpoint = apiConstants.GET_SUBSCRIPTION,
  postEndpoint = apiConstants.POST_SUBSCRIPTION,
  modules = manageStatusNotifications,
} = {}) => {
  const { handleSubmit, reset, control, formState: { isDirty } } = useForm({
    defaultValues: { sms: [], whatsapp: [], email: [] },
  });
  const [loading, setLoading] = useState(false);
  const merchant = getMerchant();
  const merchantId = merchant.id ? merchant.id : '';
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = (data) => {
    setLoading(true);
    const payload = {
      subscriptions: {
        WHATSAPP: data.whatsapp === false ? [] : data.whatsapp,
        SMS: data.sms === false ? [] : data.sms,
        EMAIL: data.email === false ? [] : data.email,
      },
    };

    post(
      `${apiConstants.BASE_LOCAL}${postEndpoint}/${merchantId}`,
      payload
    )
      .then((response) => {
        loadSubscription();
        if (
          response?.data?.responseObj === 'Alerts updated Successfully.'
        ) {
          enqueueSnackbar('Preference updated successfully', {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(response?.data?.responseObj, {
            variant: 'error',
          });
        }

        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (err && err.error) {
          enqueueSnackbar(err.error, { variant: 'error' });
        } else if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          enqueueSnackbar(err.response.data.message, { variant: 'error' });
        } else if (err && err.message) {
          enqueueSnackbar(err.message, { variant: 'error' });
        }
        setLoading(false);
      });
  };

  const loadSubscription = () => {
    setLoading(true);
    get(
      `${apiConstants.BASE_LOCAL}${getEndpoint}?merchantOid=${merchantId}`
    )
      .then((response) => {
        setLoading(false);
        if (response.data && response.data.subscriptions) {
          let defaultValues = {};
          defaultValues.whatsapp = response.data.subscriptions.WHATSAPP
            ? response.data.subscriptions.WHATSAPP
            : [];
          defaultValues.sms = response.data.subscriptions.SMS
            ? response.data.subscriptions.SMS
            : [];
          defaultValues.email = response.data.subscriptions.EMAIL
            ? response.data.subscriptions.EMAIL
            : [];
          reset({ ...defaultValues });
        }
      })
      .catch((err) => {
        console.error(err);
        if (err && err.error) {
          enqueueSnackbar(err.error, { variant: 'error' });
        } else if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          enqueueSnackbar(err.response.data.message, { variant: 'error' });
        } else if (err && err.message) {
          enqueueSnackbar(err.message, { variant: 'error' });
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    loadSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading && <Loader />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <TableContainer
          sx={{
            border: `1px solid ${BORDER}`,
            borderRadius: '10px',
            bgcolor: '#fff',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={headerCellSx}>Event</TableCell>
                <TableCell sx={headerCellSx}>Notification</TableCell>
                <TableCell sx={headerCellSx} align="center">
                  SMS
                </TableCell>
                <TableCell sx={headerCellSx} align="center">
                  WhatsApp
                </TableCell>
                <TableCell sx={headerCellSx} align="center">
                  Email
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modules.map((item) => (
                <TableRow
                  key={item.val}
                  sx={{ '&:hover': { bgcolor: '#f8f9ff' } }}
                >
                  <TableCell sx={eventCellSx}>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: WEIGHT.semibold,
                        fontSize: SIZE['2xs'],
                        color: NAVY,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </TableCell>
                  <TableCell sx={msgCellSx}>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '11.5px',
                        color: SUBTLE,
                        lineHeight: 1.5,
                      }}
                    >
                      {item.msg}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={checkCellSx}>
                    <Controller
                      name="sms"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          size="small"
                          checked={
                            Array.isArray(field.value) &&
                            field.value.includes(item.val)
                          }
                          onChange={(e) => {
                            const current = Array.isArray(field.value)
                              ? field.value
                              : [];
                            if (e.target.checked) {
                              field.onChange([...current, item.val]);
                            } else {
                              field.onChange(
                                current.filter((v) => v !== item.val)
                              );
                            }
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell align="center" sx={checkCellSx}>
                    <Controller
                      name="whatsapp"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          size="small"
                          checked={
                            Array.isArray(field.value) &&
                            field.value.includes(item.val)
                          }
                          onChange={(e) => {
                            const current = Array.isArray(field.value)
                              ? field.value
                              : [];
                            if (e.target.checked) {
                              field.onChange([...current, item.val]);
                            } else {
                              field.onChange(
                                current.filter((v) => v !== item.val)
                              );
                            }
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell align="center" sx={checkCellSx}>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          size="small"
                          checked={
                            Array.isArray(field.value) &&
                            field.value.includes(item.val)
                          }
                          onChange={(e) => {
                            const current = Array.isArray(field.value)
                              ? field.value
                              : [];
                            if (e.target.checked) {
                              field.onChange([...current, item.val]);
                            } else {
                              field.onChange(
                                current.filter((v) => v !== item.val)
                              );
                            }
                          }}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button type="submit" variant="contained" disabled={!isDirty} sx={{ ...primaryBtnSx, '&.Mui-disabled': { bgcolor: '#ccc', color: '#fff' } }}>
            Save Preferences
          </Button>
        </Box>
      </form>
    </>
  );
};

export default B2CNotification;
